import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { GlobalMultiSelectField } from '../../../components/GlobalComponents/GlobalMultiSelectField';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { useBatchStore } from '../../../stateManagers/stores/batchStore';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

// ──────────────────────────────────────────────────────────────────────────────

interface TeacherBatchAssignmentModalProps {
  visible: boolean;
  teacherId: string;
  teacherName?: string;
  instituteId?: string;
  branchId?: string;
  onHide: () => void;
  onSuccess?: () => void;
}

interface BatchOption {
  label: string;
  value: string;
  courseId?: string;
  batchTime?: string;
}

const TeacherBatchAssignmentModal: React.FC<TeacherBatchAssignmentModalProps> = ({
  visible,
  teacherId,
  teacherName,
  instituteId,
  branchId,
  onHide,
  onSuccess,
}) => {
  const { batches, fetchBatches } = useBatchStore();

  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [success, setSuccess]   = useState<string | null>(null);

  const [formData, setFormData] = useState({ batch_ids: [] as string[] });
  const [errors, setErrors]     = useState<Record<string, string>>({});

  const [allBatches, setAllBatches]                   = useState<BatchOption[]>([]);
  const [assignedBatches, setAssignedBatches]         = useState<any[]>([]);
  const [loadingAssignedBatches, setLoadingAssignedBatches] = useState(false);

  useEffect(() => {
    if (visible && (!instituteId || !branchId)) {
      setError('Institute or Branch information is missing. Please try again.');
    }
  }, [visible, instituteId, branchId]);

  useEffect(() => {
    if (visible) {
      if (!batches) fetchBatches();
      fetchAssignedBatches();
    }
  }, [visible, batches, fetchBatches]);

  const fetchAssignedBatches = async () => {
    if (!teacherId) return;
    try {
      setLoadingAssignedBatches(true);
      const response = await apiClient.get(`/v1/teacher-batches/by-teacher/${teacherId}`);
      if (response.data.success) setAssignedBatches(response.data.data);
    } catch (err: any) {
      console.error('Failed to fetch assigned batches:', err);
    } finally {
      setLoadingAssignedBatches(false);
    }
  };

  useEffect(() => {
    if (batches) {
      const assignedBatchIds = assignedBatches.map(ab => ab.batch_id);
      const filtered = batches
        .filter(batch => batch.is_active && !assignedBatchIds.includes(batch.batch_id))
        .map(batch => ({
          label: `${batch.batch_name} (${batch.batch_time})`,
          value: batch.batch_id,
          courseId: batch.course_id,
          batchTime: batch.batch_time,
        }));
      setAllBatches(filtered);
      if (formData.batch_ids.length > 0) {
        const validBatchIds = formData.batch_ids.filter(id => filtered.some(b => b.value === id));
        if (validBatchIds.length !== formData.batch_ids.length)
          setFormData(prev => ({ ...prev, batch_ids: validBatchIds }));
      }
    } else {
      setAllBatches([]);
    }
  }, [batches, assignedBatches]);

  const handleBatchChange = (value: string[]) => {
    setFormData(prev => ({ ...prev, batch_ids: value }));
    if (errors.batch_ids) setErrors(prev => ({ ...prev, batch_ids: '' }));
    setError(null);
    setSuccess(null);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!instituteId)               newErrors.general   = 'Institute ID is missing';
    if (!branchId)                  newErrors.general   = 'Branch ID is missing';
    if (formData.batch_ids.length === 0) newErrors.batch_ids = 'Please select at least one batch';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) { setError('Please fill all required fields'); return; }
    if (!instituteId || !branchId) { setError('Institute or Branch information is missing'); return; }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.post('/v1/teacher-batches', {
        institute_id: instituteId,
        branch_id:    branchId,
        teacher_id:   teacherId,
        batch_ids:    formData.batch_ids,
      });

      if (response.data.success) {
        const { assigned_count, skipped_count } = response.data.data;
        let msg = `Successfully assigned ${assigned_count} batch${assigned_count > 1 ? 'es' : ''}!`;
        if (skipped_count > 0)
          msg += ` (${skipped_count} batch${skipped_count > 1 ? 'es were' : ' was'} already assigned)`;
        setSuccess(msg);
        setTimeout(() => { handleClose(); if (onSuccess) onSuccess(); }, 2000);
      } else {
        setError(response.data.message || 'Failed to assign batches');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to assign batches');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ batch_ids: [] });
    setErrors({});
    setError(null);
    setSuccess(null);
    setAssignedBatches([]);
    onHide();
  };

  const canAssign = !!(instituteId && branchId);

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '700px' }}
      header={null}
      dismissableMask
      draggable={false}
      pt={{
        root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.3)' } },
        header:  { style: { display: 'none' } },
        content: { style: { padding: '24px', background: C.bg } },
      }}
    >
      <div>

        {/* ── Hero Header ──────────────────────────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '16px', padding: '24px', marginBottom: '20px',
          color: C.white, position: 'relative', overflow: 'hidden',
          boxShadow: '0 8px 24px rgba(14,34,93,0.2)',
        }}>
          <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '160px', height: '160px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-30px', left: '25%', width: '100px', height: '100px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '10px', fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
              <i className="pi pi-users" style={{ fontSize: '0.65rem' }} />
              Batch Assignment
            </div>
            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700', color: C.white, letterSpacing: '-0.3px' }}>
              Assign Batches to Teacher
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>
              {teacherName ? `Assigning batches to ${teacherName}` : 'Assign one or more batches to this teacher'}
            </p>
          </div>
        </div>

        {/* ── Success ──────────────────────────────────────────────────────── */}
        {success && (
          <Message severity="success" text={success} style={{ marginBottom: '16px', fontSize: '0.9rem', fontWeight: '500', width: '100%' }} />
        )}

        {/* ── Error ────────────────────────────────────────────────────────── */}
        {error && (
          <Message severity="error" text={error} style={{ marginBottom: '16px', fontSize: '0.9rem', fontWeight: '500', width: '100%' }} />
        )}

        {/* ── Currently Assigned Batches ────────────────────────────────────── */}
        {assignedBatches.length > 0 && (
          <div style={{ background: C.white, borderRadius: '14px', padding: '16px', marginBottom: '16px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.05)' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '0.75rem', fontWeight: '700', color: C.navy, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="pi pi-info-circle" style={{ color: C.gold }} />
              Currently Assigned Batches ({assignedBatches.length})
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {assignedBatches.map((assignment, idx) => (
                <div key={idx} style={{ padding: '5px 12px', background: C.bg, borderRadius: '8px', fontSize: '0.75rem', color: C.navy, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600' }}>
                  <i className="pi pi-check-circle" style={{ fontSize: '0.7rem', color: C.gold }} />
                  {assignment.batch_details?.batch_name || assignment.batch_id}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Form ─────────────────────────────────────────────────────────── */}
        {canAssign ? (
          <div style={{ background: C.white, borderRadius: '14px', padding: '20px', marginBottom: '16px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.05)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              <GlobalMultiSelectField
                label="Batches"
                value={formData.batch_ids}
                onChange={handleBatchChange}
                options={allBatches}
                placeholder="Select batches to assign"
                required
                disabled={allBatches.length === 0}
                error={errors.batch_ids}
                icon="pi pi-users"
                display="chip"
                emptyMessage="No available batches (all may be assigned already)"
                filterPlaceholder="Search batches..."
              />

              {/* Assignment Summary */}
              {formData.batch_ids.length > 0 && (
                <div style={{ padding: '16px', background: C.bg, borderRadius: '12px', border: `1px dashed ${C.goldBorder}` }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '0.75rem', fontWeight: '700', color: C.navy, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="pi pi-check-circle" style={{ color: C.gold }} />
                    Assignment Summary
                  </h4>

                  <div style={{ fontSize: '0.85rem', color: C.sub, display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <span>Batches Selected</span>
                    <span style={{ fontWeight: '700', color: C.navy }}>{formData.batch_ids.length} batch{formData.batch_ids.length > 1 ? 'es' : ''}</span>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {formData.batch_ids.map(batchId => {
                      const batch = allBatches.find(b => b.value === batchId);
                      return (
                        <div key={batchId} style={{ padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, color: C.navy, borderRadius: '8px', fontSize: '0.75rem', fontWeight: '600' }}>
                          {batch?.label || batchId}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

            </div>
          </div>

        ) : (
          <div style={{ padding: '24px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, marginBottom: '16px' }}>
            <div style={{ width: '48px', height: '48px', background: C.goldDim, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.4rem', color: C.gold }} />
            </div>
            <p style={{ margin: 0, color: C.sub, fontSize: '0.9rem' }}>
              Institute or Branch information is missing. Please ensure you're logged in properly.
            </p>
          </div>
        )}

        {/* ── Actions ──────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {/* Cancel */}
          <button
            onClick={handleClose}
            disabled={loading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 24px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '10px', color: C.navy, fontSize: '0.88rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', opacity: loading ? 0.5 : 1 }}
            onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = C.navy; (e.currentTarget as HTMLButtonElement).style.color = C.white; } }}
            onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = C.navy; } }}
          >
            <i className="pi pi-times" style={{ fontSize: '0.88rem' }} />
            Cancel
          </button>

          {/* Primary — gold */}
          <button
            onClick={handleSubmit}
            disabled={loading || !canAssign}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 24px', background: (loading || !canAssign) ? C.muted : C.gold, border: 'none', borderRadius: '10px', color: (loading || !canAssign) ? C.white : C.navy, fontSize: '0.88rem', fontWeight: '700', cursor: (loading || !canAssign) ? 'not-allowed' : 'pointer', boxShadow: (loading || !canAssign) ? 'none' : '0 4px 12px rgba(251,178,31,0.4)', transition: 'all 0.2s' }}
            onMouseEnter={e => { if (!loading && canAssign) { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,178,31,0.5)'; } }}
            onMouseLeave={e => { if (!loading && canAssign) { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(251,178,31,0.4)'; } }}
          >
            <i className={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'} style={{ fontSize: '0.88rem' }} />
            {loading ? 'Assigning...' : 'Assign Batches'}
          </button>
        </div>

      </div>
    </Dialog>
  );
};

export default TeacherBatchAssignmentModal;


// import React, { useState, useEffect } from 'react';
// import { Dialog } from 'primereact/dialog';
// import { Message } from 'primereact/message';
// import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
// import { GlobalMultiSelectField } from '../../../components/GlobalComponents/GlobalMultiSelectField';
// import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
// import { useBatchStore } from '../../../stateManagers/stores/batchStore';

// interface TeacherBatchAssignmentModalProps {
//   visible: boolean;
//   teacherId: string;
//   teacherName?: string;
//   instituteId?: string;
//   branchId?: string;
//   onHide: () => void;
//   onSuccess?: () => void;
// }

// interface BatchOption {
//   label: string;
//   value: string;
//   courseId?: string;
//   batchTime?: string;
// }

// const TeacherBatchAssignmentModal: React.FC<TeacherBatchAssignmentModalProps> = ({
//   visible,
//   teacherId,
//   teacherName,
//   instituteId,
//   branchId,
//   onHide,
//   onSuccess
// }) => {
//   const { batches, fetchBatches } = useBatchStore();

//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [success, setSuccess] = useState<string | null>(null);

//   const [formData, setFormData] = useState({
//     batch_ids: [] as string[]
//   });

//   const [errors, setErrors] = useState<Record<string, string>>({});
//   const [allBatches, setAllBatches] = useState<BatchOption[]>([]);
//   const [assignedBatches, setAssignedBatches] = useState<any[]>([]);
//   const [loadingAssignedBatches, setLoadingAssignedBatches] = useState(false);

//   // Check if instituteId and branchId are available
//   useEffect(() => {
//     if (visible && (!instituteId || !branchId)) {
//       setError('Institute or Branch information is missing. Please try again.');
//     }
//   }, [visible, instituteId, branchId]);

//   // Fetch batches and already assigned batches when modal opens
//   useEffect(() => {
//     if (visible) {
//       if (!batches) {
//         fetchBatches();
//       }
//       fetchAssignedBatches();
//     }
//   }, [visible, batches, fetchBatches]);

//   // Fetch already assigned batches
//   const fetchAssignedBatches = async () => {
//     if (!teacherId) return;
    
//     try {
//       setLoadingAssignedBatches(true);
//       const response = await apiClient.get(`/v1/teacher-batches/by-teacher/${teacherId}`);
      
//       if (response.data.success) {
//         setAssignedBatches(response.data.data);
//       }
//     } catch (err: any) {
//       console.error('Failed to fetch assigned batches:', err);
//     } finally {
//       setLoadingAssignedBatches(false);
//     }
//   };

//   // Filter batches to exclude already assigned ones
//   useEffect(() => {
//     if (batches) {
//       const assignedBatchIds = assignedBatches.map(ab => ab.batch_id);
      
//       const filtered = batches
//         .filter(batch => 
//           batch.is_active &&
//           !assignedBatchIds.includes(batch.batch_id) // Exclude already assigned batches
//         )
//         .map(batch => ({
//           label: `${batch.batch_name} (${batch.batch_time})`,
//           value: batch.batch_id,
//           courseId: batch.course_id,
//           batchTime: batch.batch_time
//         }));
      
//       setAllBatches(filtered);
      
//       // Clear batch selection if selected batches are no longer available
//       if (formData.batch_ids.length > 0) {
//         const validBatchIds = formData.batch_ids.filter(batchId =>
//           filtered.some(b => b.value === batchId)
//         );
//         if (validBatchIds.length !== formData.batch_ids.length) {
//           setFormData(prev => ({ ...prev, batch_ids: validBatchIds }));
//         }
//       }
//     } else {
//       setAllBatches([]);
//     }
//   }, [batches, assignedBatches]);

//   const handleBatchChange = (value: string[]) => {
//     setFormData(prev => ({ ...prev, batch_ids: value }));
//     // Clear error when user makes selection
//     if (errors.batch_ids) {
//       setErrors(prev => ({ ...prev, batch_ids: '' }));
//     }
//     setError(null);
//     setSuccess(null);
//   };

//   const validateForm = () => {
//     const newErrors: Record<string, string> = {};

//     if (!instituteId) {
//       newErrors.general = 'Institute ID is missing';
//     }
//     if (!branchId) {
//       newErrors.general = 'Branch ID is missing';
//     }
//     if (formData.batch_ids.length === 0) {
//       newErrors.batch_ids = 'Please select at least one batch';
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   const handleSubmit = async () => {
//     if (!validateForm()) {
//       setError('Please fill all required fields');
//       return;
//     }

//     // Extra check for TypeScript
//     if (!instituteId || !branchId) {
//       setError('Institute or Branch information is missing');
//       return;
//     }

//     setLoading(true);
//     setError(null);
//     setSuccess(null);

//     try {
//       const payload = {
//         institute_id: instituteId,
//         branch_id: branchId,
//         teacher_id: teacherId,
//         batch_ids: formData.batch_ids
//       };

//       console.log('Teacher batch assignment payload:', payload);

//       const response = await apiClient.post('/v1/teacher-batches', payload);

//       console.log('Assignment response:', response.data);

//       if (response.data.success) {
//         const { assigned_count, skipped_count } = response.data.data;
        
//         let successMessage = `Successfully assigned ${assigned_count} batch${assigned_count > 1 ? 'es' : ''}!`;
//         if (skipped_count > 0) {
//           successMessage += ` (${skipped_count} batch${skipped_count > 1 ? 'es were' : ' was'} already assigned)`;
//         }
        
//         setSuccess(successMessage);
        
//         setTimeout(() => {
//           handleClose();
//           if (onSuccess) {
//             onSuccess();
//           }
//         }, 2000);
//       } else {
//         setError(response.data.message || 'Failed to assign batches');
//       }
//     } catch (err: any) {
//       console.error('Assignment error:', err);
//       console.error('Error response:', err.response?.data);
      
//       const errorMessage = 
//         err.response?.data?.error || 
//         err.response?.data?.message || 
//         err.message || 
//         'Failed to assign batches';
//       setError(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleClose = () => {
//     setFormData({
//       batch_ids: []
//     });
//     setErrors({});
//     setError(null);
//     setSuccess(null);
//     setAssignedBatches([]);
//     onHide();
//   };

//   // Don't render form if institute/branch IDs are missing
//   const canAssign = instituteId && branchId;

//   return (
//     <Dialog
//       visible={visible}
//       onHide={handleClose}
//       style={{ width: '90vw', maxWidth: '700px' }}
//       dismissableMask
//       draggable={false}
//       pt={{
//         root: {
//           style: {
//             borderRadius: '20px',
//             overflow: 'hidden',
//             boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
//           }
//         }
//       }}
//     >
//       <div>
//         {/* Modal Header */}
//         <div style={{
//           background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//           borderRadius: '16px',
//           padding: '24px',
//           marginBottom: '24px',
//           color: 'white',
//           position: 'relative',
//           overflow: 'hidden'
//         }}>
//           <div style={{
//             position: 'absolute',
//             top: '-50px',
//             right: '-50px',
//             width: '150px',
//             height: '150px',
//             background: 'rgba(212, 241, 126, 0.1)',
//             borderRadius: '50%',
//             filter: 'blur(40px)'
//           }} />
          
//           <div style={{ position: 'relative', zIndex: 1 }}>
//             <div style={{
//               display: 'inline-block',
//               padding: '4px 10px',
//               background: 'rgba(212, 241, 126, 0.2)',
//               borderRadius: '16px',
//               marginBottom: '8px',
//               fontSize: '0.65rem',
//               fontWeight: '600',
//               letterSpacing: '0.5px',
//               textTransform: 'uppercase'
//             }}>
//               Batch Assignment
//             </div>
//             <h2 style={{
//               margin: '0 0 6px 0',
//               fontSize: '1.4rem',
//               fontWeight: '700',
//               color: 'white'
//             }}>
//               Assign Batches to Teacher
//             </h2>
//             <p style={{
//               margin: '0',
//               color: 'rgba(255,255,255,0.8)',
//               fontSize: '0.8rem'
//             }}>
//               {teacherName ? `Assigning batches to ${teacherName}` : 'Assign one or more batches to this teacher'}
//             </p>
//           </div>
//         </div>

//         {/* Success Message */}
//         {success && (
//           <Message
//             severity="success"
//             text={success}
//             style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
//           />
//         )}

//         {/* Error Message */}
//         {error && (
//           <Message
//             severity="error"
//             text={error}
//             style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
//           />
//         )}

//         {/* Currently Assigned Batches */}
//         {assignedBatches.length > 0 && (
//           <div style={{
//             background: '#f0f9ff',
//             borderRadius: '12px',
//             padding: '16px',
//             marginBottom: '20px',
//             border: '1px solid #bae6fd'
//           }}>
//             <h4 style={{
//               margin: '0 0 12px 0',
//               fontSize: '0.85rem',
//               fontWeight: '600',
//               color: '#0369a1',
//               display: 'flex',
//               alignItems: 'center',
//               gap: '8px'
//             }}>
//               <i className="pi pi-info-circle"></i>
//               Currently Assigned Batches ({assignedBatches.length})
//             </h4>
//             <div style={{
//               display: 'flex',
//               flexWrap: 'wrap',
//               gap: '8px'
//             }}>
//               {assignedBatches.map((assignment, idx) => (
//                 <div
//                   key={idx}
//                   style={{
//                     padding: '6px 12px',
//                     background: 'white',
//                     borderRadius: '8px',
//                     fontSize: '0.75rem',
//                     color: '#0369a1',
//                     border: '1px solid #bae6fd',
//                     display: 'flex',
//                     alignItems: 'center',
//                     gap: '6px'
//                   }}
//                 >
//                   <i className="pi pi-check-circle" style={{ fontSize: '0.7rem' }}></i>
//                   {assignment.batch_details?.batch_name || assignment.batch_id}
//                 </div>
//               ))}
//             </div>
//           </div>
//         )}

//         {/* Form Content */}
//         {canAssign ? (
//           <div style={{
//             background: 'white',
//             borderRadius: '12px',
//             padding: '20px',
//             marginBottom: '20px',
//             border: '1px solid #f0f0f0'
//           }}>
//             <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
//               {/* Batch Selection using GlobalMultiSelectField */}
//               <GlobalMultiSelectField
//                 label="Batches"
//                 value={formData.batch_ids}
//                 onChange={handleBatchChange}
//                 options={allBatches}
//                 placeholder="Select batches to assign"
//                 required={true}
//                 disabled={allBatches.length === 0}
//                 error={errors.batch_ids}
//                 icon="pi pi-users"
//                 display="chip"
//                 emptyMessage="No available batches (all may be assigned already)"
//                 filterPlaceholder="Search batches..."
//               />

//               {/* Assignment Summary */}
//               {formData.batch_ids.length > 0 && (
//                 <div style={{
//                   marginTop: '8px',
//                   padding: '16px',
//                   background: 'rgba(15, 61, 62, 0.05)',
//                   borderRadius: '12px',
//                   border: '1px dashed #0f3d3e'
//                 }}>
//                   <h4 style={{
//                     margin: '0 0 12px 0',
//                     fontSize: '0.85rem',
//                     fontWeight: '600',
//                     color: '#0f3d3e',
//                     display: 'flex',
//                     alignItems: 'center',
//                     gap: '8px'
//                   }}>
//                     <i className="pi pi-check-circle"></i>
//                     Assignment Summary
//                   </h4>
//                   <div style={{ fontSize: '0.85rem', lineHeight: '1.6', color: '#666' }}>
//                     <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
//                       <strong>Batches Selected:</strong>
//                       <span>{formData.batch_ids.length} batch{formData.batch_ids.length > 1 ? 'es' : ''}</span>
//                     </div>
//                   </div>
                  
//                   {/* Selected Batches List */}
//                   <div style={{ marginTop: '12px' }}>
//                     <strong style={{ fontSize: '0.75rem', color: '#666' }}>Selected Batches:</strong>
//                     <div style={{
//                       display: 'flex',
//                       flexWrap: 'wrap',
//                       gap: '6px',
//                       marginTop: '8px'
//                     }}>
//                       {formData.batch_ids.map(batchId => {
//                         const batch = allBatches.find(b => b.value === batchId);
//                         return (
//                           <div
//                             key={batchId}
//                             style={{
//                               padding: '4px 10px',
//                               background: '#d4f17e',
//                               color: '#0f3d3e',
//                               borderRadius: '6px',
//                               fontSize: '0.75rem',
//                               fontWeight: '500'
//                             }}
//                           >
//                             {batch?.label || batchId}
//                           </div>
//                         );
//                       })}
//                     </div>
//                   </div>
//                 </div>
//               )}
//             </div>
//           </div>
//         ) : (
//           <div style={{
//             padding: '20px',
//             textAlign: 'center',
//             color: '#666'
//           }}>
//             <i className="pi pi-exclamation-triangle" style={{ fontSize: '2rem', color: '#ffc107', marginBottom: '12px' }}></i>
//             <p>Institute or Branch information is missing. Please ensure you're logged in properly.</p>
//           </div>
//         )}

//         {/* Modal Actions */}
//         <div style={{
//           display: 'flex',
//           gap: '12px',
//           justifyContent: 'flex-end',
//           flexWrap: 'wrap'
//         }}>
//           <GlobalButton
//             label="Cancel"
//             variant="outline"
//             icon="pi pi-times"
//             onClick={handleClose}
//             disabled={loading}
//           />
//           <GlobalButton
//             label={loading ? 'Assigning...' : 'Assign Batches'}
//             variant="primary"
//             icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
//             onClick={handleSubmit}
//             disabled={loading || !canAssign}
//             loading={loading}
//           />
//         </div>
//       </div>
//     </Dialog>
//   );
// };

// export default TeacherBatchAssignmentModal;